import Base from '../../../Core/Base.js';
import DateHelper from '../../../Core/helper/DateHelper.js';
import Delayable from '../../../Core/mixin/Delayable.js';
import Events from '../../../Core/mixin/Events.js';
import Tooltip from '../../../Core/widget/Tooltip.js';

/**
 * @module Gantt/util/chart/TimelineChartProviderBase
 */

/**
 * This class provides a base API for the chart providers. Chart provider is responsible for
 * actually drawing the canvas and the chart. It is provided with data and view details by the
 * timeline chart feature.
 *
 * Chart canvas (implemented by subclasses) will be sized according to the timeline size. Width will
 * be equal to the time axis size and height - to the timeline subgrid height. Canvas will be scrollable
 * in horizontal direction and will remain at the top of the timeline view on vertical scroll.
 *
 * @extends Core/Base
 * @mixes Core/mixin/Events
 * @mixes Core/mixin/Delayable
 * @abstract
 */
export default class TimelineChartProviderBase extends Base.mixin(Events, Delayable) {
    static configurable = {
        disabled : false,

        width  : null,
        height : null,

        /**
         * Defines vertical margins for the chart, set to 0 to fill entire view.
         * @config {Number}
         * @internal
         * @category Chart
         */
        chartVerticalMargin : 10,

        /**
         * Template used to generate tooltip contents when chart point is hovered.
         *
         * ```javascript
         * const gantt = new Gantt({
         *     features : {
         *         timelineChart : {
         *             chartProvider : {
         *                 tooltipTemplate({ dataset, date }) {
         *                     return `
         *                         <div>${dataset.label}: ${dataset.percent}%</div>
         *                         <div>${DateHelper.format(date, this.gantt.displayDateFormat)}</div>
         *                     `;
         *                 }
         *             }
         *         }
         *     }
         * });
         * ```
         * @config {Function} tooltipTemplate
         * @param {Object} data Tooltip data
         * @param {Object} data.dataset Object with data attributes and their values read from the hovered HTML element
         * @param {Date} data.date Currently hovered date rounded to the tick start
         * @returns {String}
         */
        tooltipTemplate : function({ dataset, date }) {
            return `
                <div>${dataset.label}: ${dataset.percent}%</div>
                <div>${DateHelper.format(date, this.gantt.displayDateFormat)}</div>
            `;
        },

        /**
         * Property allowing to customize the tooltip appearance.
         *
         * @prp {Core.widget.Tooltip}
         * @accepts {TooltipConfig}
         */
        tooltip : {
            $config : ['nullify'],
            value   : {}
        }
    };

    static delayable = {
        refresh : 'raf'
    };

    construct({ gantt, config }) {
        this.gantt = gantt;

        super.construct(config);

        gantt.ion({
            timelineViewportResize : 'handleGanttResize',
            thisObj                : this
        });
    }

    //#region Config handlers

    updateDisabled(disabled) {
        if (disabled) {
            this.clear();
        }
        else {
            this.refresh();
        }
    }

    changeTooltip(tip, oldTip) {
        const me = this;

        oldTip?.destroy();

        if (tip) {
            return Tooltip.new({
                getHtml     : me.getTipHtml.bind(me),
                cls         : me.tooltipCls,
                rootElement : me.gantt.rootElement,
                owner       : me.gantt,
                defaults    : {
                    type : 'tooltip'
                }
            }, tip);
        }

        return null;
    }

    //#endregion

    getTipHtml({ activeTarget, event }) {
        return this.tooltipTemplate({
            date    : this.gantt.getDateFromCoordinate(event.clientX, 'round', false),
            dataset : activeTarget.dataset
        });
    }

    update(viewConfig, dataset) {
        const me = this;

        Object.assign(me, viewConfig);

        me.dataset = dataset;

        me.refresh();
    }

    // Chart view coordinates is a subregion of the chart element which is aligned horizontally with
    // start/end (if possible) and has some vertical margins
    getChartViewCoordinates() {
        const me = this;

        return {
            x0 : me.projectStartX,
            y0 : me.chartVerticalMargin,
            x1 : me.projectEndX,
            y1 : me.height - me.chartVerticalMargin
        };
    }

    refresh() {
        if (this.disabled) {
            return;
        }

        this.updateCanvas();
        this.draw();
        this.trigger('refresh');
    }

    clear() {}

    draw() {}

    updateCanvas() {}

    handleGanttResize({ height, oldHeight }) {
        if (oldHeight && height !== oldHeight) {
            this.height = Math.round(height);
            this.refresh();
        }
    }

    handleViewScroll(top) {
        this.element.style.top = `${top}px`;
    }
}
